// Sonido del aviso de incidente nuevo. Sintetizado con Web Audio API, igual que el
// ping de conversación nueva (MetaTemplates/utils/notificationSound.js), pero
// distinto a propósito: cuatro notas alternando alta y baja, con timbre de campanilla
// FM (tipo piano eléctrico o vidrio) y eco de sala, para que un incidente no se
// confunda con un chat nuevo sin mirar la pantalla.
let audioContext;
let primeInstalled = false;
let reverbContext;
let reverbNode;

// Gestos que sirven para despertar el audio (política de autoplay del navegador).
const GESTURE_EVENTS = ['pointerdown', 'keydown', 'touchstart'];

// Cuatro notas, alta y baja alternando, una cada 0,3 s.
const NOTE_FREQS = [950, 700, 950, 700];
const NOTE_STEP = 0.3;
/** Cuánto resuena cada nota antes de apagarse (se solapan, como una campanilla). */
const RING = 0.6;
/**
 * Volumen pico por nota. Es el que se escuchó y aprobó en la página de muestras (control
 * al 70 %): 0,5 × 0,7 × 0,3. Subirlo o bajarlo es cambiar solo este número.
 */
const PEAK = 0.105;
/** Parte de la señal que va al eco de sala. */
const REVERB_SEND = 0.28;

const getAudioContext = () => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!audioContext) audioContext = new AudioContextClass();
  return audioContext;
};

/** Eco de sala sintetizado (ruido que decae), una sola vez por contexto. */
const getReverb = (ctx) => {
  if (reverbNode && reverbContext === ctx) return reverbNode;
  const length = Math.floor(ctx.sampleRate * 1.8);
  const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let channel = 0; channel < 2; channel += 1) {
    const data = impulse.getChannelData(channel);
    for (let i = 0; i < length; i += 1) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 2.6;
    }
  }
  const convolver = ctx.createConvolver();
  convolver.buffer = impulse;
  convolver.connect(ctx.destination);
  reverbContext = ctx;
  reverbNode = convolver;
  return convolver;
};

/**
 * El aviso llega solo (sondeo), sin ningún clic de por medio: si el audio no se
 * despertó antes con un gesto, el navegador lo deja mudo. Esto engancha el
 * desbloqueo al primer gesto de la persona en la página. Es idempotente.
 */
export const primeIncidentSound = () => {
  if (primeInstalled || typeof document === 'undefined') return;
  primeInstalled = true;
  const unlock = () => {
    GESTURE_EVENTS.forEach((name) => document.removeEventListener(name, unlock, true));
    try {
      const ctx = getAudioContext();
      if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
    } catch {
      // sin audio disponible: el aviso visual sigue funcionando
    }
  };
  GESTURE_EVENTS.forEach((name) => document.addEventListener(name, unlock, true));
};

/**
 * Una nota de campanilla FM: un oscilador modula la frecuencia de otro y la
 * modulación se apaga rápido, así el golpe es brillante y después queda un tono
 * limpio. Un segundo tono a la octava le da brillo.
 */
const playBellNote = (ctx, reverb, frequency, start) => {
  const carrier = ctx.createOscillator();
  const octave = ctx.createOscillator();
  const modulator = ctx.createOscillator();
  const modulation = ctx.createGain();
  const octaveGain = ctx.createGain();
  const envelope = ctx.createGain();
  const send = ctx.createGain();

  carrier.frequency.value = frequency;
  octave.frequency.value = frequency * 2;
  modulator.frequency.value = frequency * 2;
  modulation.gain.setValueAtTime(frequency * 1.6, start);
  modulation.gain.exponentialRampToValueAtTime(frequency * 0.08, start + 0.3);
  modulator.connect(modulation);
  modulation.connect(carrier.frequency);

  octaveGain.gain.value = 0.16;
  octave.connect(octaveGain);
  octaveGain.connect(envelope);
  carrier.connect(envelope);

  envelope.gain.setValueAtTime(0.0001, start);
  envelope.gain.exponentialRampToValueAtTime(PEAK, start + 0.005);
  envelope.gain.exponentialRampToValueAtTime(0.0005, start + RING);

  envelope.connect(ctx.destination);
  send.gain.value = REVERB_SEND;
  envelope.connect(send);
  send.connect(reverb);

  [carrier, octave, modulator].forEach((oscillator) => {
    oscillator.start(start);
    oscillator.stop(start + RING + 0.05);
  });
};

/**
 * Cuatro notas de campanilla alternando alta y baja. Falla en silencio si el
 * navegador bloquea el audio: la tarjeta y la bolita roja ya avisan, el sonido es
 * un refuerzo.
 */
export const playNewIncidentSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});

    const reverb = getReverb(ctx);
    const now = ctx.currentTime;
    NOTE_FREQS.forEach((frequency, index) => {
      playBellNote(ctx, reverb, frequency, now + index * NOTE_STEP);
    });
  } catch (error) {
    console.error('No se pudo reproducir el sonido de incidente:', error);
  }
};
